import React from "react";
import { Link } from "react-router-dom";
import WidgetFrame from "./WidgetFrame";
import EmptyRow from "./EmptyRow";
import { relativeShort, AI_ANALYST_PATH, type AnalystInsight } from "../../utils/operationsDashboard";

interface AnalystInsightsWidgetProps {
  /** One sentence per agent, newest first — see latestAnalystInsights. */
  insights: AnalystInsight[];
  isLoading?: boolean;
  className?: string;
}

// The card is now the tall one in its column, so it scrolls through a real backlog rather than four rows
const VISIBLE_LIMIT = 8;

// What the agents already said, in their own words — read off answers they wrote, so nothing here costs a model call
const AnalystInsightsWidget: React.FC<AnalystInsightsWidgetProps> = ({
  insights,
  isLoading,
  className,
}) => (
  <WidgetFrame
    title="Agent answers"
    count={insights.length}
    isLoading={isLoading}
    actionLabel="Open"
    actionHref={AI_ANALYST_PATH}
    padBody={false}
    scrollBody
    className={className}
  >
    {insights.length === 0 ? (
      <div className="px-5 py-3">
        <EmptyRow>No answers yet — a scheduled run will fill this in.</EmptyRow>
      </div>
    ) : (
      /* px-3 + the row's own px-2 puts the text on the header's 20px content left, as Alerts does */
      <div className="flex flex-col gap-2 px-3 py-2">
        {insights.slice(0, VISIBLE_LIMIT).map((insight) => (
          <Link
            key={insight.id}
            to={insight.href}
            /* The tint is the ground, so hover moves the border, as the agent rows do — a second ground reads as a different state */
            className="flex flex-col gap-1 rounded-md border border-transparent bg-eai-surface-hover px-2 py-2 transition-colors hover:border-eai-cta-primary active:border-eai-cta-primary"
          >
            {/* Unclamped: the frame scrolls, so a long sentence wraps in full rather than being cut mid-clause */}
            <p className="font-inter text-eai-body-sm leading-snug text-eai-ink-soft">
              {insight.text}
            </p>
            {/* Who and when, at opposite edges — the age lands in the same column as every other card's */}
            <p className="flex min-w-0 items-center justify-between gap-2 font-inter text-eai-body-xs text-eai-ink-faint">
              <span className="truncate font-medium text-eai-ink-muted">{insight.agentLabel}</span>
              <span className="shrink-0">{relativeShort(new Date(insight.at).getTime(), Date.now())}</span>
            </p>
          </Link>
        ))}
      </div>
    )}
  </WidgetFrame>
);

export default AnalystInsightsWidget;
